import React from 'react';
import InsightList, { splitItems, joinItems } from '../InsightList';
import { INSIGHT_QUESTIONS, UNFILED } from '../../constants/insightQuestions';

// The three insight categories, each tying its question set to the two columns
// that store it. The `*_text` column is the old flat copy, still written on
// every save until a later change drops it (see the migration's fallback note).
export const INSIGHT_CATEGORIES = [
  { category: 'rr',         label: 'R&R / Partnership', itemsField: 'rr_items',         textField: 'rr_text' },
  { category: 'market',     label: 'Market Insights',   itemsField: 'market_items',     textField: 'market_insights_text' },
  { category: 'challenges', label: 'Challenges',        itemsField: 'challenges_items', textField: 'challenges_text' },
];

const answersOf = (items, id) => (Array.isArray(items?.[id]) ? items[id] : []);

/**
 * The flat TEXT value a category's items collapse to: every answer, in question
 * order with the unfiled bucket last, one per line. Answers stored under a
 * question this build does not know are deliberately NOT flattened — the text
 * copy is a legacy mirror, and inventing an order for unknown keys would make
 * it churn. The items object stays the record of truth.
 */
export function flattenCategory(category, items) {
  const ids = [...(INSIGHT_QUESTIONS[category] || []).map(q => q.id), UNFILED];
  return joinItems(ids.flatMap(id => answersOf(items, id)));
}

/**
 * One insight category, grouped by the question each answer belongs to.
 * `items` is the category's stored object; `onSave` receives the WHOLE object
 * back with a single question's array replaced, so saving one question can
 * never drop another's answers — including a question id this build cannot
 * render, which is spread through untouched.
 */
export default function InsightQuestion({ category, items, editable, onSave }) {
  const meta = INSIGHT_CATEGORIES.find(c => c.category === category);
  const questions = INSIGHT_QUESTIONS[category] || [];

  const saveQuestion = (id) => (text) => {
    const next = { ...(items || {}) };
    const answers = splitItems(text);
    // An emptied question is removed rather than stored as [], matching what
    // the API's sanitiser would do to it anyway.
    if (answers.length) next[id] = answers; else delete next[id];
    onSave(next);
  };

  const block = (id, label, extraClass) => (
    <div className={'mq-q' + (extraClass ? ' ' + extraClass : '')} key={id} data-testid={`q-${id}`}>
      <InsightList
        label={label}
        text={joinItems(answersOf(items, id))}
        editable={editable}
        onSave={saveQuestion(id)}
      />
    </div>
  );

  return (
    <div className="mq" data-testid={`insights-${category}`}>
      <span className="md-eyebrow mq-cat">{meta?.label || category}</span>
      {questions.map(q => block(q.id, q.label))}
      {/* Only shown when it holds something: an empty "unfiled" prompt would
          invite people to file answers against a non-question. */}
      {answersOf(items, UNFILED).length > 0 &&
        block(UNFILED, 'Not filed against a question', 'mq-q-unfiled')}
    </div>
  );
}
